import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  LinearProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Analytics as AnalyticsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  AreaChart,
  Area,
} from 'recharts';

import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { analyzeTopic, clearAnalysis } from '../store/slices/topicAnalysisSlice';
import { showError, showSuccess } from '../store/slices/toastSlice';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const TopicAnalysis: React.FC = () => {
  const dispatch = useAppDispatch();
  const { analysis, isLoading, error } = useAppSelector((state) => state.topicAnalysis);
  const [topic, setTopic] = useState('');

  const handleAnalyze = async () => {
    if (!topic.trim()) {
      dispatch(showError({
        title: 'Invalid Input',
        message: 'Please enter a topic to analyze',
        duration: 3000,
      }));
      return;
    }

    try {
      await dispatch(analyzeTopic({ topic: topic.trim() })).unwrap();
      dispatch(showSuccess({
        title: 'Analysis Complete',
        message: `Successfully analyzed topic: ${topic}`,
        duration: 3000,
      }));
    } catch (err) {
      // Error is handled by the slice
    }
  };

  const handleClear = () => {
    dispatch(clearAnalysis());
    setTopic('');
  };

  const renderChart = (graph: any) => {
    const { type, data } = graph;

    switch (type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={Object.keys(data[0] || {})[0]} />
              <YAxis />
              <Tooltip />
              <Bar dataKey={Object.keys(data[0] || {})[1]} fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={Object.keys(data[0] || {})[0]} />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey={Object.keys(data[0] || {})[1]} stroke="#8884d8" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey={Object.keys(data[0] || {})[1]}
              >
                {data.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'scatter':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={Object.keys(data[0] || {})[0]} />
              <YAxis dataKey={Object.keys(data[0] || {})[1]} />
              <Tooltip />
              <Scatter dataKey={Object.keys(data[0] || {})[1]} fill="#8884d8" />
            </ScatterChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={Object.keys(data[0] || {})[0]} />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey={Object.keys(data[0] || {})[1]} stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
            </AreaChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <Box display="flex" justifyContent="center" alignItems="center" height={300}>
            <Typography color="text.secondary">Unsupported chart type: {type}</Typography>
          </Box>
        );
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <AnalyticsIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Topic Analysis
          </Typography>
        </Box>
        {analysis && (
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleClear}
          >
            Clear Analysis
          </Button>
        )}
      </Box>

      {/* Input Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Analyze a Topic
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              label="Enter a topic to analyze"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              fullWidth
              placeholder="e.g., medical, technology, finance, education..."
              disabled={isLoading}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAnalyze();
                }
              }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleAnalyze}
              disabled={isLoading || !topic.trim()}
              sx={{ minWidth: 120 }}
            >
              {isLoading ? 'Analyzing...' : 'Analyze'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Loading State */}
      {isLoading && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Analyzing Topic...
            </Typography>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Please wait while we analyze "{topic}" and generate insights.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6">Analysis Failed</Typography>
          <Typography>{error}</Typography>
        </Alert>
      )}

      {/* Analysis Results */}
      {analysis && !isLoading && (
        <>
          {/* Topic Overview */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Typography variant="h5">
                  Analysis Results for: <strong>{analysis.topic}</strong>
                </Typography>
                <Chip
                  label={new Date(analysis.generated_at).toLocaleString()}
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1" paragraph>
                {analysis.description}
              </Typography>
            </CardContent>
          </Card>

          {/* Charts */}
          <Grid container spacing={3}>
            {analysis.graphs.map((graph, index) => (
              <Grid item xs={12} lg={6} key={index}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {graph.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {graph.description}
                    </Typography>
                    {renderChart(graph)}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Empty State */}
      {!analysis && !isLoading && !error && (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <AnalyticsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Analysis Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter a topic above to get started with AI-powered analysis and insights.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default TopicAnalysis;
